import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Package, Globe } from 'lucide-react';

export const DropshippingPortfolio: React.FC = () => {
  const technologies = [
    'WooCommerce', 'WordPress', 'PHP', 'MySQL', 'Stripe API', 'AJAX'
  ];

  const features = [
    'Automated Product Import',
    'Inventory Synchronization',
    'Multi-supplier Management',
    'Order Automation',
    'Profit Calculator',
    'Customer Portal',
    'Analytics Dashboard',
    'Mobile Optimization'
  ];

  const results = [
    { metric: 'Monthly Revenue', value: '$150K', description: 'Average monthly revenue achieved' },
    { metric: 'Product Catalog', value: '10,000+', description: 'Products automatically managed' },
    { metric: 'Order Processing', value: '95%', description: 'Automated order fulfillment' },
    { metric: 'Profit Margin', value: '35%', description: 'Average profit margin maintained' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Dropshipping Business
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                TrendMart Global
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A fully automated dropshipping platform managing 10,000+ products with automated 
                supplier integration, order processing, and inventory management generating $150K monthly revenue.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="E-commerce Dashboard"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  TrendMart Global wanted to build a scalable dropshipping business that could handle 
                  thousands of products from multiple suppliers while maintaining competitive pricing 
                  and fast order processing. They needed complete automation to minimize manual work.
                </p>
                <p className="mb-6">
                  We developed a comprehensive WooCommerce-based platform with custom PHP integrations 
                  for automated product imports, real-time inventory synchronization, and seamless order 
                  processing with multiple suppliers. The system includes advanced analytics and profit 
                  tracking capabilities.
                </p>
                <p>
                  The platform now manages over 10,000 products automatically, processes 95% of orders 
                  without manual intervention, and generates an average of $150K monthly revenue.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">14 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">October 2023</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Suppliers:</span>
                    <span className="font-medium">15+ integrated</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete automation suite for managing a large-scale dropshipping operation efficiently.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Exceptional growth and automation efficiency across all business metrics.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Package className="h-8 w-8 text-blue-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Multi-Supplier Integration</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Integrating with 15+ suppliers with different APIs, data formats, and update frequencies 
                while maintaining real-time inventory accuracy.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed a flexible API integration framework with standardized 
                data processing and automated error handling for seamless multi-supplier management.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Globe className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Scalable Performance</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Ensuring the platform could handle 10,000+ products with frequent updates while 
                maintaining fast page load times and search functionality.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Implemented advanced caching strategies, database optimization, 
                and AJAX-powered search with intelligent indexing for optimal performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "The automation platform OutstandingNet built has been incredible. We're now processing 
            thousands of orders monthly with minimal manual work, and our revenue has grown to $150K per month."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Alex Thompson"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">Alex Thompson</div>
              <div className="text-primary-200">Founder, TrendMart Global</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Build Your E-commerce Empire?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create an automated e-commerce solution that scales with your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};